import { useTranslation } from "react-i18next";
import { Table } from "components/Table";
import { Icon, Menu } from "@mindlercare/ui-web";
import { styled } from "styled-components";
import { useCallback } from "react";
import { TableMessage } from "./types";
import { useMessageTableData } from "./useMessageTableData";
import { useFlag } from "@mindlercare/unleash-client";
import { prefetchSignedUrl } from "../MessageModal/hooks";

type Props = {
  data: TableMessage[];
  onClick: (value: string | null) => void;
  onDelete: (messageId: string) => void;
};

const MenuItems = styled(Menu.Items)`
  transform: translateX(-50%);
`;

const Placeholder = styled.div`
  height: 40px;
`;

export const MessageTable = ({ data, onClick, onDelete }: Props) => {
  const { t } = useTranslation();

  const isDeleteMessageEnabled = useFlag("PSYCHOLOGIST_DELETE_MESSAGES");

  const { columns, rows } = useMessageTableData(data);

  const prefetchSignedUrls = async (rowId: number) => {
    const data = rows[rowId];

    if (!data.attachmentKeys) return;

    for (const key of data.attachmentKeys) {
      await prefetchSignedUrl(key);
    }
  };

  const handleRowClick = useCallback(
    (row: TableMessage) => {
      if (data.length === 0) {
        return;
      }
      if (row.status?.isDeleted) {
        return;
      }
      onClick(row.id);
    },
    [onClick, data.length],
  );

  const handleRowAction = useCallback(
    (row: TableMessage) => {
      if (row.status?.isDeleted || !isDeleteMessageEnabled) {
        return <Placeholder />;
      }

      return (
        <Menu>
          <Menu.Trigger variant="icon" testId="deleteMessageEllipsis">
            <Icon.MoreVert />
          </Menu.Trigger>
          <MenuItems>
            <Menu.Item
              onClick={() => {
                onDelete(row.id);
              }}
            >
              {t("DELETE")}
            </Menu.Item>
          </MenuItems>
        </Menu>
      );
    },
    [onDelete, t, isDeleteMessageEnabled],
  );

  return (
    <div>
      <Table
        columns={columns}
        data={rows}
        onRowClick={handleRowClick}
        rowAction={handleRowAction}
        onRowHover={prefetchSignedUrls}
      />
    </div>
  );
};
